import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, BarChart, Bar, ComposedChart, Area, AreaChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line, Cell
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import GaugeCard from "../components/ui/GaugeCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { serviceStress, serviceZoneBreakdown } from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import { useIntelligence } from "../intelligence/useIntelligence";
import { LiveServiceSection } from "../components/live/LiveOpsSections";
import { StaticContentDivider } from "../components/live/primitives";
import {
  AlertTriangle, CheckCircle, Clock, Zap, Droplets, Utensils,
  Trash2, Bolt, Wind, ChevronDown, ChevronUp, TrendingUp, TrendingDown
} from "lucide-react";

// ── inline data ─────────────────────────────────────────────────
const STRESS_TREND = [
  { time:"05:00", toilets:28, water:22, shelters:38, medical:18, food:32, parking:24, waste:18, busbays:22 },
  { time:"06:00", toilets:42, water:35, shelters:54, medical:26, food:48, parking:36, waste:28, busbays:38 },
  { time:"07:00", toilets:58, water:48, shelters:68, medical:38, food:62, parking:52, waste:41, busbays:54 },
  { time:"08:00", toilets:71, water:58, shelters:78, medical:48, food:74, parking:64, waste:52, busbays:66 },
  { time:"09:00", toilets:81, water:66, shelters:86, medical:55, food:80, parking:72, waste:61, busbays:74 },
  { time:"10:00", toilets:87, water:72, shelters:91, medical:60, food:84, parking:76, waste:68, busbays:80 },
  { time:"11:00", toilets:89, water:74, shelters:93, medical:61, food:82, parking:71, waste:66, busbays:78 },
  { time:"12:00", toilets:82, water:68, shelters:88, medical:54, food:78, parking:68, waste:61, busbays:74 },
  { time:"13:00", toilets:76, water:62, shelters:84, medical:49, food:74, parking:64, waste:56, busbays:70 },
  { time:"14:00", toilets:82, water:68, shelters:88, medical:54, food:78, parking:68, waste:61, busbays:74 },
];

const FORECAST = [
  { time:"15:00", toilets:88, water:74, shelters:94, medical:62, food:84, parking:76 },
  { time:"16:00", toilets:92, water:80, shelters:96, medical:68, food:88, parking:80 },
  { time:"17:00", toilets:86, water:74, shelters:91, medical:61, food:82, parking:72 },
  { time:"18:00", toilets:72, water:62, shelters:80, medical:52, food:70, parking:60 },
  { time:"19:00", toilets:58, water:50, shelters:66, medical:41, food:58, parking:48 },
  { time:"20:00", toilets:44, water:38, shelters:52, medical:30, food:44, parking:36 },
];

const RESTOCKING_QUEUE = [
  { id:"RST-241", facility:"Toilet Block T-14 — Ramkund Sector 3", type:"Cleaning", priority:"HIGH",   eta:"14:55", team:"NMC T-3" },
  { id:"RST-240", facility:"Water Station W-08 — Panchvati Entry", type:"Refill",   priority:"HIGH",   eta:"14:48", team:"Tanker TK-14" },
  { id:"RST-239", facility:"Food Zone FZ-06 — Trimbakeshwar",      type:"Restock",  priority:"MED",    eta:"15:10", team:"FSSAI + Vendor" },
  { id:"RST-238", facility:"Waste Point WP-22 — Ramkund Gate 4",  type:"Collection",priority:"HIGH",  eta:"14:52", team:"Waste Van W-7" },
  { id:"RST-237", facility:"Shelter Camp SC-03 — Nashik Road",     type:"Bedding",  priority:"MED",    eta:"15:30", team:"Revenue R-4" },
  { id:"RST-236", facility:"Drinking Water DW-18 — Panchvati",    type:"Quality",  priority:"CRITICAL",eta:"Now",  team:"Lab Team L-1" },
];

const WATER_QUALITY = [
  { zone:"Ramkund Ghat",    turbidity:"0.8 NTU", chlorine:"0.6 mg/L", ph:7.2, coliform:"ND", status:"Pass" },
  { zone:"Panchvati",       turbidity:"1.2 NTU", chlorine:"0.5 mg/L", ph:7.4, coliform:"ND", status:"Pass" },
  { zone:"Trimbakeshwar",   turbidity:"0.9 NTU", chlorine:"0.7 mg/L", ph:7.1, coliform:"ND", status:"Pass" },
  { zone:"Nashik Road Stn", turbidity:"1.8 NTU", chlorine:"0.3 mg/L", ph:7.6, coliform:"Trace",status:"Watch"},
  { zone:"Kalaram Temple",  turbidity:"0.7 NTU", chlorine:"0.6 mg/L", ph:7.3, coliform:"ND", status:"Pass" },
  { zone:"Gangapur Camp",   turbidity:"0.6 NTU", chlorine:"0.8 mg/L", ph:7.0, coliform:"ND", status:"Pass" },
];

const POWER_STATUS = [
  { zone:"Ramkund Ghat",    grid:"Online", dg:"Standby", ups:98, critFacilities:12, load:84 },
  { zone:"Panchvati",       grid:"Online", dg:"Standby", ups:96, critFacilities:9,  load:76 },
  { zone:"Trimbakeshwar",   grid:"Online", dg:"Running", ups:88, critFacilities:8,  load:92 },
  { zone:"Nashik Road Stn", grid:"Online", dg:"Standby", ups:99, critFacilities:6,  load:68 },
  { zone:"Kalaram Temple",  grid:"Online", dg:"Standby", ups:94, critFacilities:5,  load:60 },
  { zone:"Gangapur Camp",   grid:"Online", dg:"Standby", ups:97, critFacilities:4,  load:48 },
];

const SHELTER_CAMPS = [
  { id:"SC-01", name:"Camp Alpha — Ramkund",     capacity:5000,  occupied:4820, status:"critical", zone:"Ramkund" },
  { id:"SC-02", name:"Camp Beta — Panchvati",    capacity:4500,  occupied:3960, status:"high",     zone:"Panchvati" },
  { id:"SC-03", name:"Camp Gamma — Trimbak",     capacity:3800,  occupied:3420, status:"high",     zone:"Trimbakeshwar" },
  { id:"SC-04", name:"Camp Delta — Nashik Rd",   capacity:6200,  occupied:4960, status:"moderate", zone:"Nashik Road" },
  { id:"SC-05", name:"Camp Epsilon — Kalaram",   capacity:3200,  occupied:2240, status:"normal",   zone:"Kalaram" },
  { id:"SC-06", name:"Camp Zeta — Gangapur",     capacity:4800,  occupied:2880, status:"normal",   zone:"Gangapur" },
  { id:"SC-07", name:"Overflow OFZ-A — Ring Rd", capacity:2400,  occupied:820,  status:"normal",   zone:"Overflow" },
  { id:"SC-08", name:"Overflow OFZ-B — NH3",     capacity:3000,  occupied:480,  status:"normal",   zone:"Overflow" },
];

const FOOD_SAFETY = [
  { id:"FS-08", stall:"Vendor V-14 — Ramkund FZ-2", issue:"Temperature violation >65°C",   action:"Fined + warned",    severity:"HIGH",   time:"13:42" },
  { id:"FS-07", stall:"Stall ST-22 — Panchvati",    issue:"Unsafe water source detected",   action:"Closed for inspection",severity:"CRITICAL",time:"14:08"},
  { id:"FS-06", stall:"Vendor V-31 — Trimbak",      issue:"Labelling non-compliance",       action:"Advisory issued",   severity:"LOW",    time:"13:20" },
  { id:"FS-05", stall:"Camp Kitchen CK-1 — Gangapur",issue:"Routine inspection — passed",   action:"Certificate issued",severity:"CLEAR",  time:"12:55" },
];

const HISTORICAL_COMPARISON = [
  { label:"Kumbh 2015", toilets:78, water:84, shelters:96, medical:62, food:88 },
  { label:"Kumbh 2019", toilets:84, water:78, shelters:91, medical:58, food:84 },
  { label:"2027 Now",   toilets:89, water:74, shelters:93, medical:61, food:82 },
];

const WASTE_BY_ZONE = [
  { zone:"Ramkund Ghat",    collected:28.4, pending:4.2, pct:87 },
  { zone:"Panchvati",       collected:18.6, pending:2.8, pct:87 },
  { zone:"Trimbakeshwar",   collected:14.2, pending:1.8, pct:89 },
  { zone:"Nashik Road Stn", collected:11.8, pending:1.4, pct:89 },
  { zone:"Kalaram Temple",  collected:8.4,  pending:0.8, pct:91 },
  { zone:"Gangapur Camp",   collected:6.2,  pending:0.4, pct:94 },
];

// ── helpers ──────────────────────────────────────────────────────
// Historical Benchmark card uses raw lowercase field keys as row labels —
// map them to their display words so they can be translated.
const HISTORICAL_KEY_LABEL = { toilets:"Toilets", water:"Water", shelters:"Shelters", medical:"Medical", food:"Food" };

// Service-load vocabulary (critical/high/moderate/normal) -> shared status
// bucket, so a "critical" service card, badge and radar bar all agree.
const serviceBucket = s =>
  s === "critical" ? "critical" :
  s === "high"     ? "serious"  :
  s === "moderate" ? "warning"  : "good";
const sColor = s => statusHex(serviceBucket(s));
const sBg = s => statusClasses(serviceBucket(s));

// Card-border tint uses a neutral (not emerald) fallback for "normal" —
// distinct bucket from sBg/sColor above, matching the original's intent.
const serviceBorderBucket = s =>
  s === "critical" ? "critical" :
  s === "high"     ? "serious"  :
  s === "moderate" ? "warning"  : "neutral";
const SERVICE_WASH = { critical:"bg-red-50/30", serious:"bg-orange-50/20", warning:"bg-amber-50/10", neutral:"" };
const sBorder = s => `${STATUS[serviceBorderBucket(s)].border} ${SERVICE_WASH[serviceBorderBucket(s)]}`.trim();

// Heatmap cell tint by numeric load — same 90/75/55 bands as the table's
// caption, now resolved through the shared threshold + status palette.
const fillCell = v => {
  const b = thresholdStatus(v, [{ min:90, bucket:"critical" }, { min:75, bucket:"warning" }, { min:55, bucket:"info" }, { min:0, bucket:"good" }]);
  return { background: STATUS[b].hex + "22", color: STATUS[b].hex };
};

// ── component ────────────────────────────────────────────────────
export default function ServiceMonitoring() {
  const { services } = useIntelligence({ running: false });
  const [expandedService, setExpandedService] = useState(null);
  const t = useTranslate();

  // Derived from the real service/queue arrays instead of hardcoded, so these
  // can't silently disagree with the cards/tables rendered below them.
  const criticalCount     = serviceStress.filter(s => s.status === "critical").length;
  const highOrAboveCount  = serviceStress.filter(s => s.status === "high" || s.status === "critical").length;
  const totalFacilities   = serviceStress.reduce((sum, s) => sum + (parseInt(s.count.replace(/[^0-9]/g, ""), 10) || 0), 0);
  const activeAlertsCount = RESTOCKING_QUEUE.length + FOOD_SAFETY.filter(f => f.severity !== "CLEAR").length;
  const avgPlatformLoad   = Math.round(serviceStress.reduce((sum, s) => sum + s.used, 0) / serviceStress.length);
  const powerCoveragePct  = Math.round(POWER_STATUS.filter(p => p.grid === "Online").length / POWER_STATUS.length * 100);

  return (
    <div className="space-y-8">
      <SectionHeader icon="Layers" title="Service Stress Intelligence"
        subtitle="360° NMC · water · toilets · shelters · food zones · medical camps · parking · waste · power · water quality · restocking queue · predictive load · FSSAI food safety · historical benchmarks"
      />


      <LiveServiceSection services={services} />

      <StaticContentDivider />
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Critical Services"  value={criticalCount.toString()}         icon="AlertTriangle" color="red"    index={0} live />
        <MetricCard title="High Stress"        value={highOrAboveCount.toString()}      icon="TrendingUp"    color="amber"  index={1} />
        <MetricCard title="Total Facilities"   value={totalFacilities.toLocaleString()} icon="Building2"     color="blue"   index={2} />
        <MetricCard title="Active Alerts"      value={activeAlertsCount.toString()}     icon="Zap"           color="red"    index={3} live />
        <MetricCard title="Restocking Pending" value={RESTOCKING_QUEUE.length.toString()} icon="RefreshCw"   color="amber"  index={4} />
        <MetricCard title="Avg Platform Load"  value={`${avgPlatformLoad}%`}            icon="Activity"      color="amber"  index={5} />
        <MetricCard title="Food Safety Clear"  value="96%"     icon="CheckCircle"   color="green"  index={6} />
        <MetricCard title="Power Coverage"     value={`${powerCoveragePct}%`}           icon="Bolt"          color="green"  index={7} />
      </div>

      {/* NMC Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-amber-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">{t("NMC SERVICE COMMAND — 14:33 IST —")} {totalFacilities.toLocaleString()} {t("FACILITIES MONITORED")}</span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Nashik Municipal Corporation — Service Stress Intelligence")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              {criticalCount} {t("critical ·")} {highOrAboveCount} {t("high-stress · AI predicts shelter peak at 16:00 (96%) · 6 restocking orders active · water quality all-pass except Nashik Road (watch)")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label:"Shelters", val:"93%" },
              { label:"Toilets",  val:"89%" },
              { label:"Food",     val:"82%" },
              { label:"Water",    val:"74%" },
            ].map((b, i) => {
              const c = googleColor(i);
              return (
                <div key={b.label} className="rounded-xl px-3 py-2 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background: c.bgLight, border: `1px solid ${c.border}` }}>
                  <div className="text-lg font-black" style={{ color: c.text }}>{b.val}</div>
                  <div className="text-[9px]" style={{ color: c.text }}>{t(b.label)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Gauges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GaugeCard title="Shelter Load"   value={93} index={0} />
        <GaugeCard title="Toilet Usage"  value={89} index={1} />
        <GaugeCard title="Food Zone"     value={82} index={2} />
        <GaugeCard title="Water Station" value={74} index={3} />
      </div>

      {/* Service cards (expandable) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {serviceStress.map((s, i) => {
          const isOpen = expandedService === s.name;
          return (
            <motion.div key={s.name}
              initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.06 }}
              className={`card p-4 hover:shadow-card-hover transition-all cursor-pointer border ${sBorder(s.status)}`}
              onClick={() => setExpandedService(isOpen ? null : s.name)}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-700">{t(s.name)}</span>
                <div className="flex items-center gap-1">
                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${sBg(s.status)}`}>
                    {t(s.status.toUpperCase())}
                  </span>
                  {isOpen ? <ChevronUp size={10} className="text-slate-400" /> : <ChevronDown size={10} className="text-slate-400" />}
                </div>
              </div>
              <div className="text-2xl font-black mb-1" style={{ color: sColor(s.status) }}>{s.used}%</div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-2">
                <motion.div className="h-2 rounded-full" style={{ background: sColor(s.status) }}
                  initial={{ width:0 }} animate={{ width:`${s.used}%` }}
                  transition={{ duration:0.8, delay: i * 0.06 }} />
              </div>
              <div className="text-[10px] text-slate-500">{t(s.count)}</div>
              {s.used >= 85 && (
                <div className={`flex items-center gap-1 mt-1.5 text-[9px] ${STATUS.critical.text} font-bold`}>
                  <AlertTriangle size={9} /> {t("Overload warning")}
                </div>
              )}
              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                    exit={{ height:0, opacity:0 }} transition={{ duration:0.2 }}
                    className="overflow-hidden pt-2 mt-2 border-t border-slate-200 text-[9px] space-y-1">
                    <div className="flex justify-between"><span className="text-slate-500">{t("Current load")}</span><span className="font-black text-slate-800">{s.used}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t("Capacity")}</span><span className="font-black text-slate-800">{s.capacity}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t("Forecast 16:00")}</span><span className="font-black text-orange-600">+4–6%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">{t("Total units")}</span><span className="font-black text-blue-700">{t(s.count)}</span></div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Stress trend + Radar */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard title="Service Stress Trend — Today"
          subtitle="Load % across all service categories — hourly" live>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={STRESS_TREND} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} unit="%" />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="toilets"  stroke={chartColor(0)} strokeWidth={2} dot={false} name={t("Toilets")}  />
              <Line type="monotone" dataKey="water"    stroke={chartColor(1)} strokeWidth={2} dot={false} name={t("Water")}    />
              <Line type="monotone" dataKey="shelters" stroke={chartColor(2)} strokeWidth={2} dot={false} name={t("Shelters")} />
              <Line type="monotone" dataKey="medical"  stroke={chartColor(3)} strokeWidth={2} dot={false} name={t("Medical")}  />
              <Line type="monotone" dataKey="food"     stroke={chartColor(4)} strokeWidth={2} dot={false} name={t("Food")}     />
              <Line type="monotone" dataKey="waste"    stroke={chartColor(5)} strokeWidth={1.5} dot={false} name={t("Waste")} strokeDasharray="4 2" />
              <Line type="monotone" dataKey="busbays"  stroke={chartColor(6)} strokeWidth={1.5} dot={false} name={t("Bus Bays")} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Service Radar — Current Stress Profile">
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={serviceStress} margin={{ top:10 }}>
              <PolarGrid stroke={gridProps.stroke} />
              <PolarAngleAxis dataKey="name" tick={axisTick} tickFormatter={t} />
              <PolarRadiusAxis angle={30} domain={[0,100]} tick={axisTick} />
              <Radar name={t("Current Load")} dataKey="used" stroke={chartColor(0)} fill={chartColor(0)} fillOpacity={0.25} />
              <Radar name={t("Safe Threshold")} dataKey="capacity" stroke={statusHex("critical")} fill={statusHex("critical")} fillOpacity={0.05} strokeDasharray="4 2" />
              <Legend wrapperStyle={legendStyle} />
              <Tooltip content={<ChartTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Predictive Forecast */}
      <ChartCard title="Predictive Service Load Forecast — 15:00 to 20:00"
        subtitle="AI-projected service stress for afternoon/evening — Shahi Snan peak expected 16:00" live>
        <div className="mb-2 px-1">
          <span className="badge badge-warning text-[9px]">{t("AI-generated forecast — human review required before resource allocation decisions")}</span>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <ComposedChart data={FORECAST} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <defs>
              <linearGradient id="shelterGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.3} />
                <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="time" tick={axisTick} />
            <YAxis tick={axisTick} unit="%" domain={[40,100]} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={legendStyle} />
            <Area type="monotone" dataKey="shelters" fill="url(#shelterGrad)" stroke={chartColor(0)} strokeWidth={2} name={t("Shelters %")} />
            <Line type="monotone" dataKey="toilets"  stroke={chartColor(1)} strokeWidth={2} dot={{ r:3 }} name={t("Toilets %")} strokeDasharray="5 3" />
            <Line type="monotone" dataKey="water"    stroke={chartColor(2)} strokeWidth={2} dot={{ r:3 }} name={t("Water %")} strokeDasharray="5 3" />
            <Line type="monotone" dataKey="medical"  stroke={chartColor(3)} strokeWidth={2} dot={{ r:3 }} name={t("Medical %")} strokeDasharray="5 3" />
            <Bar  dataKey="food"  fill={chartColor(4)} radius={[3,3,0,0]} name={t("Food Zone %")} fillOpacity={0.6} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Zone Heatmap */}
      <ChartCard title="Service Load Heatmap — Zone × Category"
        subtitle="Color coded: red ≥90% · amber ≥75% · blue ≥55% · green <55%">
        <div className="overflow-x-auto">
          <table className="gov-table w-full text-xs">
            <thead>
              <tr>
                <th className="text-left">{t("Zone")}</th>
                {["Water","Toilets","Shelter","Food","Medical","Parking"].map(h => <th key={h} className="text-center">{t(h)}</th>)}
              </tr>
            </thead>
            <tbody>
              {serviceZoneBreakdown.map((z, i) => (
                <motion.tr key={z.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                  <td className="font-bold text-slate-900 whitespace-nowrap">
                    <Link to="/infrastructure" className="hover:text-blue-700 hover:underline underline-offset-2">{t(z.zone)}</Link>
                  </td>
                  {[z.water, z.toilets, z.shelter, z.food, z.medical, z.parking].map((v, j) => (
                    <td key={j} className="p-0">
                      <div className="mx-1 my-1 rounded-lg py-1.5 text-[11px] font-black text-center"
                        style={fillCell(v)}>{v}%</div>
                    </td>
                  ))}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Shelter camps + Restocking queue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Shelter Camp Occupancy — Live"
          subtitle="Pilgrim shelter capacity and current occupancy across all 8 camps">
          <div className="space-y-2.5">
            {SHELTER_CAMPS.map((c, i) => {
              const pct = Math.round((c.occupied / c.capacity) * 100);
              return (
                <motion.div key={c.id}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}>
                  <div className="flex items-center justify-between mb-1 gap-1">
                    <span className="text-[10px] font-semibold text-slate-700 truncate flex-1">{t(c.name)}</span>
                    <div className="flex items-center gap-1.5 flex-shrink-0 text-[9px]">
                      <span className="text-slate-500">{c.occupied.toLocaleString()}/{c.capacity.toLocaleString()}</span>
                      <span className={`font-black ${STATUS[thresholdStatus(pct, [{ min:90, bucket:"critical" }, { min:75, bucket:"warning" }, { min:0, bucket:"good" }])].text}`}>{pct}%</span>
                    </div>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div className="h-2.5 rounded-full" style={{ background: sColor(c.status) }}
                      initial={{ width:0 }} animate={{ width:`${Math.min(pct, 100)}%` }}
                      transition={{ duration:0.8, delay: i * 0.06 }} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </ChartCard>

        <ChartCard title="Restocking & Maintenance Queue"
          subtitle="Active NMC field orders for facility replenishment and repair" live>
          <div className="space-y-2">
            {RESTOCKING_QUEUE.map((r, i) => (
              <motion.div key={r.id}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`p-2.5 rounded-xl border text-xs ${
                  r.priority === "CRITICAL" ? `${STATUS.critical.border} bg-red-50/30` :
                  r.priority === "HIGH"     ? `${STATUS.warning.border} bg-amber-50/20` : STATUS.neutral.border
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="font-mono text-[9px] text-slate-400">{r.id}</span>
                      <span className={`text-[8px] font-black px-1 py-0.5 rounded-full ${
                        statusClasses(r.priority === "CRITICAL" ? "critical" : r.priority === "HIGH" ? "warning" : "info")
                      }`}>{t(r.priority)}</span>
                      <span className="badge badge-info text-[8px]">{t(r.type)}</span>
                    </div>
                    <p className="text-[10px] font-semibold text-slate-800 truncate">{t(r.facility)}</p>
                    <div className="text-[9px] text-slate-500 mt-0.5">{t("Team")}: {t(r.team)}</div>
                  </div>
                  <div className="text-right flex-shrink-0 text-[10px]">
                    <div className="font-black text-blue-700">{t("ETA")} {r.eta}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Water Quality + Power */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Drinking Water Quality — Zone Monitoring"
          subtitle="WHO/BIS parameters: turbidity · chlorine · pH · coliform — tested hourly">
          <div className="overflow-x-auto">
            <table className="gov-table w-full text-xs">
              <thead>
                <tr>
                  <th>{t("Zone")}</th><th className="text-center">{t("Turbidity")}</th>
                  <th className="text-center">{t("Chlorine")}</th><th className="text-center">pH</th>
                  <th className="text-center">{t("Coliform")}</th><th className="text-center">{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {WATER_QUALITY.map((w, i) => (
                  <motion.tr key={w.zone} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay: i * 0.05 }}>
                    <td className="font-bold text-slate-800 whitespace-nowrap">{t(w.zone)}</td>
                    <td className="text-center text-slate-600">{w.turbidity}</td>
                    <td className="text-center text-slate-600">{w.chlorine}</td>
                    <td className="text-center text-slate-600">{w.ph}</td>
                    <td className={`text-center font-bold ${w.coliform === "ND" ? STATUS.good.text : STATUS.warning.text}`}>{t(w.coliform)}</td>
                    <td className="text-center">
                      <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${
                        statusClasses(w.status === "Pass" ? "good" : w.status === "Watch" ? "warning" : "critical")
                      }`}>{t(w.status)}</span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <ChartCard title="Power Supply Status — Critical Facilities"
          subtitle="Grid · DG · UPS coverage per zone · load percentage">
          <div className="space-y-3">
            {POWER_STATUS.map((p, i) => (
              <motion.div key={p.zone}
                initial={{ opacity:0, x:4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`p-3 rounded-xl border ${p.dg === "Running" ? `${STATUS.warning.border} bg-amber-50/20` : STATUS.neutral.border}`}
              >
                <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                  <Link to="/infrastructure" className="text-xs font-bold text-slate-800 hover:text-blue-700 hover:underline underline-offset-2">{t(p.zone)}</Link>
                  <div className="flex items-center gap-1.5 text-[9px]">
                    <span className={`${STATUS.good.text} font-bold`}>{t("Grid")}: {t(p.grid)}</span>
                    <span className={`font-bold ${p.dg === "Running" ? STATUS.warning.text : "text-slate-500"}`}>{t("DG")}: {t(p.dg)}</span>
                    <span className="text-blue-700 font-bold">UPS: {p.ups}%</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div className="h-2 rounded-full" style={{ background: statusHex(thresholdStatus(p.load, [{ min:85, bucket:"critical" }, { min:70, bucket:"warning" }, { min:0, bucket:"good" }])) }}
                      initial={{ width:0 }} animate={{ width:`${p.load}%` }} transition={{ duration:0.8 }} />
                  </div>
                  <span className="text-[9px] font-black text-slate-700 flex-shrink-0 w-8">{p.load}%</span>
                  <span className="text-[9px] text-slate-500 flex-shrink-0">{p.critFacilities} {t("crit.")}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Waste + Food Safety + Historical */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Waste Collection — Zone Load">
          <div className="space-y-3">
            {WASTE_BY_ZONE.map((w, i) => (
              <motion.div key={w.zone}
                initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}>
                <div className="flex items-center justify-between mb-1 text-[10px]">
                  <span className="font-semibold text-slate-700 truncate flex-1">{t(w.zone)}</span>
                  <span className="text-slate-500 flex-shrink-0 ml-2">{w.collected}{t("T collected")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div className="h-2 rounded-full bg-emerald-500"
                      initial={{ width:0 }} animate={{ width:`${w.pct}%` }} transition={{ duration:0.8 }} />
                  </div>
                  <span className={`text-[9px] font-black flex-shrink-0 ${w.pending > 2 ? STATUS.critical.text : "text-slate-600"}`}>
                    {w.pending}{t("T pending")}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="FSSAI Food Safety Alerts"
          subtitle="Food stall inspections and violations">
          <div className="space-y-2.5">
            {FOOD_SAFETY.map((f, i) => (
              <motion.div key={f.id}
                initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.08 }}
                className={`p-2.5 rounded-xl border text-xs ${
                  f.severity === "CRITICAL" ? `${STATUS.critical.border} bg-red-50/30` :
                  f.severity === "HIGH"     ? `${STATUS.serious.border} bg-orange-50/20` :
                  f.severity === "CLEAR"    ? `${STATUS.good.border} bg-emerald-50/20` : STATUS.neutral.border
                }`}
              >
                <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                  <span className="font-mono text-[9px] text-slate-400">{f.id}</span>
                  <span className={`text-[8px] font-black px-1 py-0.5 rounded-full ${
                    statusClasses(f.severity === "CRITICAL" ? "critical" : f.severity === "HIGH" ? "serious" : f.severity === "CLEAR" ? "good" : "info")
                  }`}>{t(f.severity)}</span>
                  <span className="text-[9px] text-slate-400">{f.time}</span>
                </div>
                <p className="text-[10px] font-semibold text-slate-800 mb-0.5 truncate">{t(f.stall)}</p>
                <p className="text-[9px] text-slate-600">{t(f.issue)}</p>
                <p className="text-[9px] text-blue-700 font-bold mt-0.5">→ {t(f.action)}</p>
              </motion.div>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Historical Benchmark"
          subtitle="Service stress comparison: 2015 / 2019 / 2027">
          <div className="space-y-4 pt-1">
            {["toilets","water","shelters","medical","food"].map(key => (
              <div key={key}>
                <div className="text-[9px] font-black text-slate-500 uppercase mb-1.5">{t(HISTORICAL_KEY_LABEL[key])}</div>
                {HISTORICAL_COMPARISON.map((h, i) => (
                  <div key={h.label} className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] text-slate-500 w-20 flex-shrink-0">{t(h.label)}</span>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div className="h-2 rounded-full" style={{ background: ["#64748B","#F59E0B","#2563EB"][i] }}
                        initial={{ width:0 }} animate={{ width:`${h[key]}%` }} transition={{ duration:0.8, delay: i * 0.1 }} />
                    </div>
                    <span className="text-[9px] font-black text-slate-700 w-8 text-right flex-shrink-0">{h[key]}%</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}





